import { create } from 'zustand';
import type { User } from '@field-tickets/shared';
import {
  syncTicketRecoveryOwner,
  ticketRecoveryOwner,
} from '@/utils/ticketRecovery';
import { syncOfflineQueueOwner } from '@/utils/offlineQueue';
import { snapshotTicketForms } from '@/utils/ticketSessionSnapshot';
import { noteSignedInOwner, noteSignedOut } from '@/utils/authEpoch';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  ticketEditingFrozenEpoch: number | null;
  freezeTicketEditing: (epoch: number) => void;
  unfreezeTicketEditing: (epoch: number) => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  ticketEditingFrozenEpoch: null,
  freezeTicketEditing: (epoch) => set({ ticketEditingFrozenEpoch: epoch }),
  unfreezeTicketEditing: (epoch) => {
    if (get().ticketEditingFrozenEpoch === epoch) set({ ticketEditingFrozenEpoch: null });
  },
  setUser: (user) => {
    // Capture the departing author's latest edits before moving the epoch or
    // hiding their queue. Login changes visibility, never deletes their work.
    if (ticketRecoveryOwner(get().user) !== ticketRecoveryOwner(user)) snapshotTicketForms(true);
    noteSignedInOwner(ticketRecoveryOwner(user));
    syncTicketRecoveryOwner(ticketRecoveryOwner(user));
    syncOfflineQueueOwner(ticketRecoveryOwner(user));
    set({ user, isAuthenticated: !!user, isLoading: false,
      ticketEditingFrozenEpoch: ticketRecoveryOwner(get().user) === ticketRecoveryOwner(user) ? get().ticketEditingFrozenEpoch : null });
  },
  setLoading: (isLoading) => set({ isLoading }),
  logout: () => {
    snapshotTicketForms(true);
    noteSignedOut();
    // An empty session hides unsent work; all owners keep their own entries.
    syncTicketRecoveryOwner(null);
    syncOfflineQueueOwner(null);
    set({ user: null, isAuthenticated: false, isLoading: false, ticketEditingFrozenEpoch: null });
  },
}));
